import { getApiErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { action, observable } from 'mobx';

import { IRootStore } from '@stores/index';
import { isDestNameUnique } from '@components/destinations/destination-view/utils';
import { analytics } from '@lib/analytics';
import { BI_EVENTS, CATEGORY, TRACK_TYPE } from '@lib/analytics/events';
import { DESTINATION_CATEGORIES } from '@components/common/constants';
import { DestinationStore, IDestination, IDestinationStore } from './destination';
import DraftDestination from './draftDestination';
import { AxiosResponse } from 'axios';
import { getPermissions } from './util';
import { logBadRequest } from '@utils/errorLogger';
import { prettyPrintDestinationCategory, prettyPrintSourceCategory } from '@lib/analytics/utils';
import { ISourceStore } from './source';

interface CreatePayload {
  name: string;
  definitionName: string;
  destinationDefinitionId: string;
  config: {
    webhookUrl?: string;
  };
  enabled: boolean;
}

type ServerDestination = IDestinationStore & { permissions?: { id: string; isLocked: boolean } };

export interface IDestinationsListStore {
  destinations: IDestinationStore[];
  firstLoad: boolean;
  draftDestination: DraftDestination;
  rootStore: IRootStore;
  setDestinations(destinations: IDestinationStore[]): void;
  getDestinations(): void;
  createDestination(
    dest: CreatePayload,
    onSuccess?: (dest: IDestination) => void,
    onError?: () => void,
  ): Promise<IDestination | undefined>;
  cloneDestination(
    name: string,
    id: string,
    onSuccess: (dest: IDestination) => void,
    onError: () => void,
  ): void;
  createDestinationConnections(
    dest: IDestination,
    ids: string[],
    onSuccess?: () => void,
    sqlModelId?: string,
    onError?: () => void,
  ): Promise<boolean>;
  deleteDestination(dest: IDestination, onSuccess: () => void, onError: () => void): void;
  destinationById(destId: string): IDestinationStore | undefined;
  hasWareHouseDestinations(): boolean;
  isDestNameUnique: (newName: string, currentName?: string) => boolean;
}

export class DestinationsListStore implements IDestinationsListStore {
  @observable public destinations: IDestinationStore[] = [];

  @observable public rootStore: IRootStore;

  @observable public firstLoad = false;

  @observable public draftDestination: DraftDestination;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    this.draftDestination = new DraftDestination();
  }

  @action.bound
  public setDestinations(destinations: IDestinationStore[]): void {
    this.destinations = destinations;
  }

  @action.bound
  public async getDestinations() {
    const { token, selectedWorkspaceId } = this.rootStore.userStore;
    try {
      const res: AxiosResponse<{ destinations: ServerDestination[] }> = await apiAuthCaller(
        token,
      ).get(`/destinations?workspace_id=${selectedWorkspaceId}`);
      this.destinations = res.data.destinations.map(
        (destination) => new DestinationStore(destination, this.rootStore),
      );
      this.rootStore.permissionsStore.registerResource(getPermissions(res.data.destinations));
      this.firstLoad = true;
    } catch (err) {}
  }

  @action.bound
  public async createDestination(
    dest: CreatePayload,
    onSuccess?: (dest: IDestination) => void,
    onError?: () => void,
  ) {
    const { userStore, messagesStore, permissionsStore } = this.rootStore;
    const { token, selectedWorkspaceId } = userStore;
    try {
      const request = apiAuthCaller(token);
      logBadRequest(request);
      const res = await request.post('/destinations/', {
        name: dest.name,
        definitionName: dest.definitionName,
        // TODO: remove when validations are completed
        destinationDefinitionId: dest.destinationDefinitionId,
        config: dest.config,
        workspaceId: selectedWorkspaceId,
        enabled: dest.enabled,
      });
      const savedDest = new DestinationStore(res.data, this.rootStore);
      this.destinations.push(savedDest);
      permissionsStore.addNewResource(savedDest.id);
      if (onSuccess) {
        onSuccess(savedDest);
      } else {
        messagesStore.showSuccessMessage('Destination created successfully');
      }
      analytics.track2(BI_EVENTS.NEW_DEST, TRACK_TYPE.API_SUCCESS, {
        destination_id: savedDest.id,
        destination_type: savedDest.name,
        destination_name: savedDest.destinationDefinition.displayName,
        destination_category: prettyPrintDestinationCategory(savedDest.destinationDefinition),
      });
      return savedDest;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to create destination'));
      onError?.();
      return undefined;
    }
  }

  @action.bound
  public async cloneDestination(
    name: string,
    id: string,
    onSuccess: (dest: IDestination) => void,
    onError: () => void,
  ) {
    const { userStore, messagesStore, permissionsStore } = this.rootStore;
    const { token } = userStore;

    try {
      const r = await apiAuthCaller(token).post(`/destinations/${id}/clone`, { name });
      this.destinations.push(new DestinationStore(r.data, this.rootStore));
      permissionsStore.addNewResource(r.data.id);
      messagesStore.showSuccessMessage('Destination cloned successfully');
      onSuccess(r.data);
    } catch (e) {
      messagesStore.showErrorMessage('Failed to clone destination');
      onError();
    }
  }

  @action.bound
  public async createDestinationConnections(
    dest: IDestination,
    sourceIds: string[],
    onSuccess: () => void,
    sqlModelId?: string,
    onError?: () => void,
  ) {
    const { userStore, messagesStore, connectionsStore, sourcesListStore, modelListStore } =
      this.rootStore;
    const { token } = userStore;
    try {
      await apiAuthCaller(token).post(`/destinations/${dest.id}/connect`, {
        sourceIds,
        sqlModelId,
      });

      if (sqlModelId) {
        const sqlModel = modelListStore.getModelById(sqlModelId);
        sqlModel?.setSourceIds([...sqlModel.sourceIds, sourceIds[0]]);
      }
      onSuccess?.();
      // update connections store
      sourceIds.forEach((_, key) => {
        if (!connectionsStore.connections[sourceIds[key]]) {
          connectionsStore.connections[sourceIds[key]] = [];
        }
        connectionsStore.connections[sourceIds[key]].push(dest.id);
        const source = sourcesListStore.sourceById(sourceIds[key])!;
        this.trackConnection(dest, source, sqlModelId);
      });
      return true;
    } catch (err) {
      onError?.();
      messagesStore.showErrorMessage(
        getApiErrorMessage(err, 'Failed to connect source to destination'),
      );
      return false;
    }
  }

  private trackConnection(
    dest: IDestination,
    source: ISourceStore,
    sqlModelId: string | undefined,
  ) {
    const sourceType = source.sourceDef.displayName;
    analytics.track2(BI_EVENTS.CREATE_CONNECTION, TRACK_TYPE.API_SUCCESS, {
      dest_id: dest.id,
      dest_type: dest.destinationDefinition.displayName,
      source_id: source.id,
      source_type: sourceType,
      destination_name: dest.name,
      source_name: source.name,
      destination_category: prettyPrintDestinationCategory(dest.destinationDefinition),
      source_category: prettyPrintSourceCategory(source.sourceDef),
    });
    if (sqlModelId) {
      const useModelEvent = {
        id: sqlModelId,
        sourceId: source.id,
        destId: dest.id,
        sourceType,
      };
      analytics.track(BI_EVENTS.USE_MODEL, TRACK_TYPE.API_SUCCESS, CATEGORY.MODEL, useModelEvent);
    }
  }

  @action.bound
  public async deleteDestination(
    destination: IDestinationStore,
    onSuccess: () => void,
    onError: () => void,
  ) {
    const {
      messagesStore,
      userStore: { token },
      transformationsListStore,
    } = this.rootStore;
    try {
      await apiAuthCaller(token).delete(`/destinations/${destination.id}`);
      onSuccess();
      messagesStore.showSuccessMessage('Destination deleted successfully');
      this.destinations = this.destinations.filter(
        (existingDest) => existingDest.id !== destination.id,
      );
      transformationsListStore.getTransformations();
      window.rudderanalytics.track('destination deleted', {
        name: destination.name,
        destID: destination.id,
        destType: destination.destinationDefinition.displayName,
      });
    } catch (err) {
      onError();
      messagesStore.showErrorMessage('Failed to delete destination');
    }
  }

  @action.bound
  public destinationById(destId: string): IDestinationStore | undefined {
    return this.destinations.find((dest) => dest.id === destId);
  }

  public hasWareHouseDestinations() {
    return this.destinations.some(
      (destination: IDestinationStore) =>
        destination?.destinationDefinition.category === DESTINATION_CATEGORIES.WAREHOUSE,
    );
  }

  @action.bound
  public isDestNameUnique(newName: string, currentName = '') {
    return isDestNameUnique(newName, currentName, this.destinations);
  }
}
